import isEqual from 'lodash/isEqual';
import * as React from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
  UseFormProps,
} from 'react-hook-form';

import { useLatest } from '../../hooks/useLatest';

type Props<TFieldValues extends FieldValues> = Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> & {
  id?: string;
  className?: string;
  resetKey?: string;
  values?: DefaultValues<TFieldValues> | TFieldValues;
  /** This is a convenience prop for backward compatibility, but could also be passed through `useFormProps` */
  defaultValues?: DefaultValues<TFieldValues>;
  useFormProps?: UseFormProps<TFieldValues>;
  onSubmit: SubmitHandler<TFieldValues>;
};

export function Form<TFieldValues extends FieldValues>({
  id,
  className,
  resetKey,
  values,
  defaultValues,
  onSubmit,
  children,
  useFormProps,
  ...rest
}: React.PropsWithChildren<Props<TFieldValues>>) {
  const latestResetKey = useLatest(resetKey);
  const latestValues = useLatest(values);

  const methods = useForm({
    defaultValues,
    ...useFormProps,
  });

  const { reset } = methods;

  React.useEffect(() => {
    if (latestResetKey !== resetKey || !isEqual(values, latestValues)) {
      reset(values);
    }
  }, [values, latestValues, resetKey, latestResetKey, reset]);

  return (
    <FormProvider {...methods}>
      <form
        {...rest}
        id={id}
        className={className}
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
      >
        {children}
      </form>
    </FormProvider>
  );
}
